/**
 * School outreach utilities — engagement scoring, WhatsApp links, CSV export.
 */

import { normalizeSchool } from "./aggregateAnalytics";

const PILLARS = ["Armitage", "Insignia", "Zenith", "Orthodox"];

/** @typedef {{ key: string, label: string, whatsapp?: string, contactName?: string, notes?: string, pinned?: boolean }} SchoolContact */
/** @typedef {{ key: string, label: string, whatsapp?: string, contactName?: string, notes?: string }} ManualSchool */

export function normalizeWhatsApp(raw) {
  if (!raw) return "";
  let digits = String(raw).replace(/\D/g, "");
  if (digits.startsWith("0")) digits = "94" + digits.slice(1);
  if (!digits.startsWith("94") && digits.length === 9) digits = "94" + digits;
  return digits;
}

export function whatsAppUrl(number, message) {
  const n = normalizeWhatsApp(number);
  if (!n) return "";
  const base = `https://wa.me/${n}`;
  if (!message) return base;
  return `${base}?text=${encodeURIComponent(message)}`;
}

/**
 * Composite engagement score 0–100.
 */
export function computeEngagementScore({ count, quizTeams, hasIct, byHostingSchool }) {
  let score = 0;
  score += Math.min(count / 20, 1) * 40;
  score += (Math.min(quizTeams ?? 0, 2) / 2) * 25;
  if (hasIct) score += 15;
  const pillarCount = PILLARS.filter(
    (p) => (byHostingSchool?.[p] ?? 0) > 0
  ).length;
  score += (pillarCount / 4) * 20;
  return Math.round(score);
}

export function engagementTier(score) {
  if (score >= 75) return { id: "champion", label: "Champion", className: "tier-champion" };
  if (score >= 50) return { id: "hot", label: "Hot", className: "tier-hot" };
  if (score >= 25) return { id: "warm", label: "Warm", className: "tier-warm" };
  if (score > 0) return { id: "cold", label: "Cold", className: "tier-cold" };
  return { id: "none", label: "None", className: "tier-none" };
}

/**
 * What this school is still missing — for gap analysis column.
 */
export function registrationGaps({ count, quizTeams, hasIct, isManual, quizPending }) {
  const gaps = [];
  if (count === 0 || isManual) gaps.push("No registrations");
  if (quizPending) gaps.push("Confirm last-year quiz");
  if (quizTeams === 0) gaps.push("No quiz team");
  else if (quizTeams === 1) gaps.push("Quiz 1/2 only");
  if (!hasIct) gaps.push("No ICT Society");
  if (count > 0 && count < 3) gaps.push("Low volume");
  return gaps;
}

/**
 * Outreach urgency + human label.
 */
export function outreachStatus(row, hasContact) {
  const { count, quizTeams, hasIct, isManual, quizPending } = row;
  if (isManual && count === 0) {
    return { id: "prospect", label: "Prospect", urgency: 5, className: "outreach-prospect" };
  }
  if (count === 0) {
    return { id: "zero", label: "Zero regs", urgency: 5, className: "outreach-zero" };
  }
  if (quizPending) {
    return { id: "quiz-stale", label: "Confirm quiz", urgency: 5, className: "outreach-quiz-stale" };
  }
  if (!hasContact) {
    return { id: "no-contact", label: "Need contact", urgency: 4, className: "outreach-no-contact" };
  }
  if (quizTeams < 2) {
    return { id: "quiz-gap", label: "Quiz incomplete", urgency: 4, className: "outreach-quiz" };
  }
  if (!hasIct) {
    return { id: "ict-gap", label: "Missing ICT", urgency: 3, className: "outreach-ict" };
  }
  if (count >= 10 && quizTeams >= 2 && hasIct) {
    return { id: "star", label: "Star school", urgency: 1, className: "outreach-star" };
  }
  return { id: "active", label: "Active", urgency: 2, className: "outreach-active" };
}

export function buildSmartMessage(row) {
  const name = row.displayName;
  if (row.quizPending && row.quizStaleTeams > 0) {
    return (
      `Hi ${name}! 🎯\n\n` +
      `We're updating BTUI 2026 quiz registrations. Your school had ${row.quizStaleTeams} team` +
      `${row.quizStaleTeams !== 1 ? "s" : ""} registered last year.\n\n` +
      `Please confirm:\n` +
      `• Are the SAME team(s) returning?\n` +
      `• Or are NEW/different teams registering this year?\n` +
      `• Or are you not sending quiz teams this year?\n\n` +
      `(Schools can enter up to 2 teams for BTUI 2026.)`
    );
  }
  if (row.isManual || row.count === 0) {
    return (
      `Hello from BTUI 2026! 👋\n\n` +
      `We're reaching out to ${name} about Beyond The User Interface — Royal College's premier ICT competition.\n\n` +
      `We'd love to have your school compete this year. Can we share registration details with you?`
    );
  }
  if (row.quizTeams === 0) {
    return (
      `Hi ${name}! 🎯\n\n` +
      `Thanks for registering for BTUI 2026! We noticed you haven't signed up for the Inter School Quiz yet — schools can enter 2 teams.\n\n` +
      `Would you like help registering?`
    );
  }
  if (row.quizTeams === 1) {
    return (
      `Hi ${name}! 🎯\n\n` +
      `Great to see you at BTUI 2026 with 1 quiz team! You can register a second team — would you like to add one?`
    );
  }
  if (!row.hasIct) {
    return (
      `Hi ${name}! 🏆\n\n` +
      `You're registered for BTUI 2026 competitions — don't forget Most Popular ICT Society! It's a great way to showcase your school's ICT club.\n\n` +
      `Need the registration link?`
    );
  }
  return (
    `Hi ${name}! 🎉\n\n` +
    `Thank you for your strong participation in BTUI 2026 (${row.count} registration${row.count !== 1 ? "s" : ""}). ` +
    `If you need anything before event day, we're here to help!`
  );
}

export function contactsByKey(contacts = []) {
  const map = new Map();
  for (const c of contacts) {
    const k = normalizeSchool(c.key);
    if (k) map.set(k, c);
  }
  return map;
}

/**
 * Merge leaderboard rows with manual schools and contact info.
 */
export function buildOutreachRows(leaderboardRows, manualSchools = [], contacts = []) {
  const contactMap = contactsByKey(contacts);
  const seen = new Set();

  const enriched = leaderboardRows.map((row, i) => {
    seen.add(row.key);
    const contact = contactMap.get(row.key);
    const quizTeams = row.quizTeams ?? 0;
    const hasIct = row.hasIct ?? false;
    const quizPending = row.quizPending ?? false;
    const base = {
      ...row,
      rank: i + 1,
      quizTeams,
      hasIct,
      quizPending,
      isManual: false,
      contact,
      whatsapp: contact?.whatsapp ?? "",
      contactName: contact?.contactName ?? "",
      notes: contact?.notes ?? "",
      pinned: contact?.pinned ?? false,
    };
    const score = computeEngagementScore({
      count: base.count,
      quizTeams,
      hasIct,
      byHostingSchool: base.byHostingSchool,
    });
    return {
      ...base,
      engagementScore: score,
      tier: engagementTier(score),
      gaps: registrationGaps(base),
      outreach: outreachStatus(
        { ...base, quizTeams, hasIct },
        Boolean(normalizeWhatsApp(contact?.whatsapp))
      ),
      smartMessage: buildSmartMessage({ ...base, quizTeams, hasIct }),
      waLink: whatsAppUrl(contact?.whatsapp, buildSmartMessage({ ...base, quizTeams, hasIct })),
    };
  });

  for (const manual of manualSchools) {
    const key = normalizeSchool(manual.key || manual.label);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const contact = contactMap.get(key) || manual;
    const base = {
      key,
      displayName: manual.label || manual.key,
      count: 0,
      pct: "0.0",
      byHostingSchool: {},
      quizTeams: 0,
      hasIct: false,
      isManual: false,
      contact,
      whatsapp: contact?.whatsapp ?? manual.whatsapp ?? "",
      contactName: contact?.contactName ?? manual.contactName ?? "",
      notes: contact?.notes ?? manual.notes ?? "",
      pinned: contact?.pinned ?? false,
    };
    const score = 0;
    enriched.push({
      ...base,
      rank: null,
      engagementScore: score,
      tier: engagementTier(score),
      gaps: registrationGaps({ ...base, isManual: false }),
      outreach: outreachStatus(base, Boolean(normalizeWhatsApp(base.whatsapp))),
      smartMessage: buildSmartMessage(base),
      waLink: whatsAppUrl(base.whatsapp, buildSmartMessage(base)),
    });
  }

  enriched.sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    if (a.count !== b.count) return b.count - a.count;
    return a.displayName.localeCompare(b.displayName);
  });

  return enriched;
}

export const OUTREACH_FILTERS = [
  { id: "all", label: "All schools" },
  { id: "quiz-stale", label: "Old quiz teams" },
  { id: "priority", label: "🔥 Priority outreach" },
  { id: "no-contact", label: "📵 Missing contact" },
  { id: "zero", label: "0 registrations" },
  { id: "quiz-gap", label: "Quiz incomplete" },
  { id: "ict-gap", label: "Missing ICT" },
  { id: "prospect", label: "Manual prospects" },
  { id: "star", label: "⭐ Star schools" },
];

export function filterOutreachRows(rows, filterId) {
  if (filterId === "all") return rows;
  if (filterId === "quiz-stale") {
    return rows.filter((r) => r.quizPending);
  }
  if (filterId === "priority") {
    return rows.filter((r) => r.outreach.urgency >= 4);
  }
  if (filterId === "no-contact") {
    return rows.filter((r) => !normalizeWhatsApp(r.whatsapp));
  }
  if (filterId === "zero") {
    return rows.filter((r) => r.count === 0);
  }
  if (filterId === "quiz-gap") {
    return rows.filter((r) => r.quizTeams < 2);
  }
  if (filterId === "ict-gap") {
    return rows.filter((r) => !r.hasIct);
  }
  if (filterId === "prospect") {
    return rows.filter((r) => r.isManual);
  }
  if (filterId === "star") {
    return rows.filter((r) => r.outreach.id === "star");
  }
  return rows;
}

function csvEscape(val) {
  if (val === null || val === undefined) return "";
  const s = String(val);
  if (s.includes(",") || s.includes("\n") || s.includes('"')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function pickContactName(contactNameCol, contactPersonCol) {
  const name = (contactNameCol ?? "").trim();
  const person = (contactPersonCol ?? "").trim();
  if (name && person && name.toLowerCase() !== person.toLowerCase()) {
    return `${name} — ${person}`;
  }
  return person || name;
}

/** Reverse of pickContactName for CSV export. */
export function splitContactFields(stored) {
  const s = (stored ?? "").trim();
  const sep = " — ";
  const idx = s.indexOf(sep);
  if (idx >= 0) {
    return {
      contactName: s.slice(0, idx).trim(),
      contactPerson: s.slice(idx + sep.length).trim(),
    };
  }
  return { contactName: s, contactPerson: "" };
}

export const CSV_EXPORT_HEADERS = [
  "School",
  "WhatsApp",
  "Contact Name",
  "contact person",
];

/**
 * Export schools as CSV matching the import format (4 columns only).
 * @param {object[]} rows — outreach rows currently visible in the table
 */
export function exportLeaderboardCsv(rows) {
  const lines = [CSV_EXPORT_HEADERS.join(",")];

  for (const r of rows) {
    const wa = normalizeWhatsApp(r.whatsapp);
    const { contactName, contactPerson } = splitContactFields(r.contactName);
    lines.push(
      [r.displayName, wa, contactName, contactPerson].map(csvEscape).join(",")
    );
  }

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `btui_school_contacts_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// CSV import
// ---------------------------------------------------------------------------

function parseCsvRow(line, delimiter = ",") {
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === delimiter && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function detectCsvDelimiter(line) {
  const tabs = (line.match(/\t/g) || []).length;
  const commas = (line.match(/,/g) || []).length;
  return tabs > commas ? "\t" : ",";
}

function normalizeHeader(h) {
  return h.replace(/^\uFEFF/, "").trim().toLowerCase();
}

function findCsvColumn(headers, candidates) {
  const normalized = headers.map(normalizeHeader);
  for (const c of candidates) {
    const target = c.replace(/[^a-z0-9]/gi, "").toLowerCase();
    const idx = normalized.findIndex(
      (h) => h.replace(/[^a-z0-9]/gi, "").toLowerCase() === target
    );
    if (idx >= 0) return idx;
  }
  for (const c of candidates) {
    const idx = normalized.findIndex((h) => h.includes(c.toLowerCase()));
    if (idx >= 0) return idx;
  }
  return -1;
}

/**
 * Parse CSV with columns: School, WhatsApp, Contact Name, contact person
 * (comma- or tab-separated, header row required or auto-detected).
 *
 * @returns {{ rows: { label: string, whatsapp: string, contactName: string }[], skipped: number, errors: string[] }}
 */
export function parseSchoolContactsCsv(text) {
  const errors = [];
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) {
    return { rows: [], skipped: 0, errors: ["File is empty."] };
  }

  const delimiter = detectCsvDelimiter(lines[0]);
  const first = parseCsvRow(lines[0], delimiter).map(normalizeHeader);

  const schoolCol = findCsvColumn(first, [
    "school",
    "school name",
    "schoolname",
    "institution",
  ]);
  const whatsappCol = findCsvColumn(first, [
    "whatsapp",
    "whats app",
    "phone",
    "mobile",
    "contact number",
    "number",
    "tel",
  ]);
  const contactNameCol = findCsvColumn(first, [
    "contact name",
    "contactname",
  ]);
  const contactPersonCol = findCsvColumn(first, [
    "contact person",
    "contactperson",
  ]);

  const hasHeader =
    schoolCol >= 0 &&
    (whatsappCol >= 0 || contactNameCol >= 0 || contactPersonCol >= 0);

  let startLine = 0;
  let sCol = schoolCol;
  let waCol = whatsappCol;
  let cnCol = contactNameCol;
  let cpCol = contactPersonCol;

  if (!hasHeader) {
    sCol = 0;
    waCol = 1;
    cnCol = 2;
    cpCol = 3;
  } else {
    startLine = 1;
  }

  if (sCol < 0) {
    return {
      rows: [],
      skipped: 0,
      errors: ["Missing School column. Expected: School, WhatsApp, Contact Name, contact person."],
    };
  }

  const rows = [];
  let skipped = 0;

  for (let i = startLine; i < lines.length; i++) {
    const cols = parseCsvRow(lines[i], delimiter);
    const label = (cols[sCol] ?? "").trim();
    const whatsapp = waCol >= 0 ? (cols[waCol] ?? "").trim() : "";
    const contactName = pickContactName(
      cnCol >= 0 ? cols[cnCol] : "",
      cpCol >= 0 ? cols[cpCol] : ""
    );
    if (!label) {
      skipped++;
      continue;
    }
    rows.push({ label, whatsapp, contactName });
  }

  if (rows.length === 0) {
    errors.push(
      "No valid rows found. Expected columns: School, WhatsApp, Contact Name, contact person."
    );
  }

  return { rows, skipped, errors };
}

/**
 * Dedupe by normalized school key; prefer row with a contact number.
 */
export function dedupeCsvSchoolRows(rows) {
  const map = new Map();
  for (const row of rows) {
    const key = normalizeSchool(row.label);
    if (!key) continue;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, { ...row, key });
      continue;
    }
    const merged = {
      label: existing.label.length >= row.label.length ? existing.label : row.label,
      whatsapp: existing.whatsapp || row.whatsapp,
      contactName: existing.contactName || row.contactName,
      key,
    };
    if (row.contactName && existing.contactName && row.contactName !== existing.contactName) {
      merged.contactName = existing.contactName.length >= row.contactName.length
        ? existing.contactName
        : row.contactName;
    }
    map.set(key, merged);
  }
  return Array.from(map.values());
}

/**
 * Merge deduped CSV rows into saved contacts + manual roster.
 * Schools with registrations keep their count; others appear with 0 regs.
 *
 * @param {{ label: string, whatsapp: string }[]} csvRows
 * @param {{
 *   schoolContacts: SchoolContact[],
 *   manualSchools: ManualSchool[],
 *   registeredKeys: Set<string>,
 * }} existing
 */
export function mergeCsvSchoolImport(csvRows, existing) {
  const { schoolContacts = [], manualSchools = [], registeredKeys } = existing;
  const parsedCount = csvRows.length;
  const deduped = dedupeCsvSchoolRows(csvRows);
  const duplicatesRemoved = parsedCount - deduped.length;

  const contactMap = contactsByKey(schoolContacts);
  const manualMap = new Map(
    manualSchools.map((s) => [normalizeSchool(s.key || s.label), { ...s }])
  );

  let contactsUpdated = 0;
  let schoolsAdded = 0;
  let matchedRegistered = 0;

  for (const row of deduped) {
    const key = row.key || normalizeSchool(row.label);
    const label = row.label.trim();
    const prev = contactMap.get(key);
    const wa = normalizeWhatsApp(row.whatsapp) || (row.whatsapp ?? "").trim();
    const contactName = (row.contactName ?? prev?.contactName ?? "").trim();

    contactMap.set(key, {
      key,
      label,
      whatsapp: wa,
      contactName: contactName || prev?.contactName || "",
      notes: prev?.notes ?? "",
      pinned: prev?.pinned ?? false,
    });
    contactsUpdated++;

    if (registeredKeys.has(key)) {
      matchedRegistered++;
    } else if (!manualMap.has(key)) {
      manualMap.set(key, { key, label, whatsapp: wa, contactName });
      schoolsAdded++;
    } else {
      manualMap.set(key, {
        ...manualMap.get(key),
        label,
        whatsapp: wa || manualMap.get(key).whatsapp,
      });
    }
  }

  return {
    schoolContacts: Array.from(contactMap.values()),
    manualSchools: Array.from(manualMap.values()),
    stats: {
      parsed: parsedCount,
      deduped: deduped.length,
      duplicatesRemoved,
      contactsUpdated,
      schoolsAdded,
      matchedRegistered,
      skippedEmpty: 0,
    },
  };
}
